/**
 * @file LeaderboardService — returns the leaderboard for a competition.
 *
 * Mock-backed today (isLive=false). The interface already matches what the
 * future backend will provide (live leaderboard, weekly stats, ghost replays),
 * so enabling the real feed is a config change, not a rewrite.
 */

import {
  USE_MOCKS,
  MOCK_ENDPOINTS,
  API_ENDPOINTS,
} from '../config/scrWeeklyConfig';
import { getJson } from './httpClient';

/**
 * @typedef {import('../models/leaderboard').Leaderboard} Leaderboard
 * @typedef {import('../models/leaderboard').LeaderboardEntry} LeaderboardEntry
 */

/** @param {*} value @returns {Date|null} */
function toDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Normalize a raw leaderboard payload into the typed model.
 * @param {*} raw
 * @param {number} season
 * @param {number} week
 * @param {boolean} isLive
 * @returns {Leaderboard}
 */
function normalizeLeaderboard(raw, season, week, isLive) {
  const rawEntries = Array.isArray(raw && raw.entries) ? raw.entries : [];
  /** @type {LeaderboardEntry[]} */
  const entries = rawEntries.map((e, i) => ({
    rank: typeof e.rank === 'number' ? e.rank : i + 1,
    playerId: e.playerId || `player-${i + 1}`,
    displayName: e.displayName || 'Anonymous',
    country: e.country || null,
    timeMs: typeof e.timeMs === 'number' ? e.timeMs : 0,
    achievedAt: toDate(e.achievedAt),
    isCurrentPlayer: Boolean(e.isCurrentPlayer),
    ghostReplayUrl: e.ghostReplayUrl || null,
  }));

  entries.sort((a, b) => a.rank - b.rank);

  return {
    season,
    week,
    entries,
    totalPlayers:
      typeof (raw && raw.totalPlayers) === 'number'
        ? raw.totalPlayers
        : entries.length,
    updatedAt: toDate(raw && raw.updatedAt),
    isLive,
  };
}

export const LeaderboardService = {
  /**
   * Get the leaderboard for a competition.
   * @param {number} season
   * @param {number} week
   * @returns {Promise<Leaderboard>}
   */
  async getLeaderboard(season, week) {
    if (USE_MOCKS) {
      const raw = await getJson(MOCK_ENDPOINTS.leaderboard);
      return normalizeLeaderboard(raw, season, week, false);
    }
    const raw = await getJson(API_ENDPOINTS.leaderboard(season, week));
    return normalizeLeaderboard(raw, season, week, true);
  },
};
